import { useEffect, useMemo, useState } from "preact/hooks"
import useSWR from "swr"
import { Card, CardContent, CardHeader } from "../../components/ui/card"
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis,
  Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, ZAxis, Cell,
} from "recharts"
import { format, parseISO, startOfMonth, subMonths } from "date-fns"
import { getDcSummary } from "../sales_forecasting/api/distributor_sales_api"
import { DcTrendPoint, DcTotalPoint } from "../sales_forecasting/types/distributor_sales"
import { DcMap, DC_COLORS, formatAmount } from "./dc_map"
import { ChevronsUpDownIcon, ArrowUpIcon, ArrowDownIcon, Info } from "lucide-react"
import { Tooltip as UiTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip"

type DcSortField = "dc_name" | "dc_number" | "total_amount" | "invoice_count" | "order_count" | "avg_weeks_between_orders" | "normalized_weeks_per_order" | "avg_amount_per_order"
type SortDir = "asc" | "desc"
type TrendViewMode = "revenue" | "orders"
type CadencePoint = DcTotalPoint & { cadence_weeks: number; order_size: number; color: string }
interface DcOrderPoint {
  dc_name: string
  invoice_date: string
  invoice_number: string
  po_number?: string | null
  amount: number
}
type OrderTimelinePoint = DcOrderPoint & { timestamp: number; color: string }

/** Rolling 12 calendar months: first day of month 11 months ago through today (matches API invoice_date filter). */
function lastTwelveMonthRange(): { start_date: string; end_date: string } {
  return {
    start_date: format(startOfMonth(subMonths(new Date(), 11)), "yyyy-MM-dd"),
    end_date: format(new Date(), "yyyy-MM-dd"),
  }
}

export function DcDeductionSection() {
  const range = lastTwelveMonthRange()
  const { data, isLoading } = useSWR(
    ["dc-summary", "kehe", range.start_date, range.end_date],
    () => getDcSummary({ source: "kehe", start_date: range.start_date, end_date: range.end_date }),
    { revalidateOnFocus: false }
  )

  // Table sort state
  const [sortField, setSortField] = useState<DcSortField>("total_amount")
  const [sortDir, setSortDir] = useState<SortDir>("desc")
  const [trendViewMode, setTrendViewMode] = useState<TrendViewMode>("revenue")
  const [selectedDcs, setSelectedDcs] = useState<string[]>([])

  function handleSort(field: DcSortField) {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDir(field === "dc_name" || field === "dc_number" ? "asc" : "desc")
    }
  }

  // Trend rows are already limited to last 12 months via getDcSummary date params.
  const trendChartData = useMemo(() => {
    if (!data?.trend.length) return []
    const byMonth: Record<string, Record<string, number | string>> = {}
    data.trend.forEach((p: DcTrendPoint) => {
      const monthKey = p.month
      if (!byMonth[monthKey]) byMonth[monthKey] = { month: monthKey }
      byMonth[monthKey][p.dc_name] = ((byMonth[monthKey][p.dc_name] as number) || 0) + p.amount
    })
    return Object.values(byMonth).sort((a: any, b: any) => a.month.localeCompare(b.month))
  }, [data?.trend])

  const dcNames = useMemo(() => {
    if (!data?.by_dc.length) return []
    return data.by_dc.map((d: DcTotalPoint) => d.dc_name)
  }, [data?.by_dc])

  const chartConfig = useMemo(() => {
    const config: Record<string, { label: string; color: string }> = {}
    dcNames.forEach((name: string, idx: number) => {
      config[name] = { label: name, color: DC_COLORS[idx % DC_COLORS.length] }
    })
    return config
  }, [dcNames])

  useEffect(() => {
    setSelectedDcs(dcNames)
  }, [dcNames])

  const selectedDcSet = useMemo(() => new Set(selectedDcs), [selectedDcs])

  const visibleDcNames = useMemo(() => {
    if (!selectedDcs.length) return []
    return dcNames.filter((dc: string) => selectedDcSet.has(dc))
  }, [dcNames, selectedDcs, selectedDcSet])

  const orderTimelinePoints = useMemo<OrderTimelinePoint[]>(() => {
    const orderPoints = (data as ({ order_points?: DcOrderPoint[] } | undefined))?.order_points ?? []
    if (!orderPoints.length) return []
    return orderPoints
      .map((point: DcOrderPoint, idx: number) => {
        const dcIdx = dcNames.findIndex((dc: string) => dc === point.dc_name)
        const parsed = parseISO(point.invoice_date)
        return {
          ...point,
          timestamp: parsed.getTime(),
          color: DC_COLORS[(dcIdx >= 0 ? dcIdx : idx) % DC_COLORS.length],
        }
      })
      .filter((point: OrderTimelinePoint) => Number.isFinite(point.timestamp))
      .sort((a: OrderTimelinePoint, b: OrderTimelinePoint) => a.timestamp - b.timestamp)
  }, [data, dcNames])

  const visibleOrderTimelinePoints = useMemo(() => {
    return orderTimelinePoints.filter((point: OrderTimelinePoint) => selectedDcSet.has(point.dc_name))
  }, [orderTimelinePoints, selectedDcSet])

  const sortedByDc = useMemo(() => {
    if (!data?.by_dc.length) return []
    return [...data.by_dc].sort((a: DcTotalPoint, b: DcTotalPoint) => {
      let aVal: string | number
      let bVal: string | number
      if (sortField === "dc_name") { aVal = a.dc_name; bVal = b.dc_name }
      else if (sortField === "dc_number") { aVal = a.dc_number ?? ""; bVal = b.dc_number ?? "" }
      else if (sortField === "total_amount") { aVal = a.total_amount; bVal = b.total_amount }
      else if (sortField === "invoice_count") { aVal = a.invoice_count; bVal = b.invoice_count }
      else if (sortField === "order_count") { aVal = a.order_count; bVal = b.order_count }
      else if (sortField === "avg_weeks_between_orders") { aVal = a.avg_weeks_between_orders ?? -1; bVal = b.avg_weeks_between_orders ?? -1 }
      else if (sortField === "normalized_weeks_per_order") { aVal = a.normalized_weeks_per_order ?? -1; bVal = b.normalized_weeks_per_order ?? -1 }
      else { aVal = a.avg_amount_per_order ?? -1; bVal = b.avg_amount_per_order ?? -1 }

      if (typeof aVal === "string") {
        return sortDir === "asc" ? aVal.localeCompare(bVal as string) : (bVal as string).localeCompare(aVal)
      }
      return sortDir === "asc" ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number)
    })
  }, [data?.by_dc, sortField, sortDir])

  const cadenceChartData = useMemo<CadencePoint[]>(() => {
    if (!data?.by_dc.length) return []
    return data.by_dc
      .map((dc: DcTotalPoint, idx: number) => {
        const cadence_weeks = dc.avg_weeks_between_orders ?? dc.normalized_weeks_per_order
        if (cadence_weeks == null || dc.avg_amount_per_order == null) return null
        return {
          ...dc,
          cadence_weeks,
          order_size: dc.avg_amount_per_order,
          color: DC_COLORS[idx % DC_COLORS.length],
        }
      })
      .filter((dc): dc is CadencePoint => dc !== null)
  }, [data?.by_dc])

  function toggleDcSelection(dcName: string) {
    setSelectedDcs((current: string[]) =>
      current.includes(dcName) ? current.filter((dc: string) => dc !== dcName) : [...current, dcName]
    )
  }

  function selectAllDcs() {
    setSelectedDcs(dcNames)
  }

  function clearAllDcs() {
    setSelectedDcs([])
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader><h3 class="text-lg font-semibold">KeHE Sales by DC</h3></CardHeader>
        <CardContent>
          <div class="h-48 flex items-center justify-center text-gray-400">Loading DC data…</div>
        </CardContent>
      </Card>
    )
  }

  if (!data?.by_dc.length) {
    return (
      <Card>
        <CardHeader><h3 class="text-lg font-semibold">KeHE Sales by DC</h3></CardHeader>
        <CardContent>
          <div class="h-40 flex items-center justify-center text-gray-400">
            No DC invoice sales data available. DC information is populated from KeHE invoice syncs.
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div class="space-y-4">
      <h2 class="text-xl font-semibold text-gray-800">KeHE Sales by DC</h2>

      {/* Full-width map */}
      <Card>
        <CardHeader>
          <h3 class="text-base font-semibold">DC Locations</h3>
          <p class="text-sm text-gray-500">Last 12 months · bubble size reflects total invoice sales volume — hover for details</p>
        </CardHeader>
        <CardContent class="p-2">
          <DcMap data={data.by_dc} />
        </CardContent>
      </Card>

      {/* Full-width trend chart */}
      <Card>
        <CardHeader>
          <h3 class="text-base font-semibold">Monthly Trend by DC</h3>
          <p class="text-sm text-gray-500">
            {trendViewMode === "revenue"
              ? "Last 12 months · deduction invoice amounts by month"
              : "Last 12 months · each order plotted by invoice date and amount"}
          </p>
        </CardHeader>
        <CardContent>
          <div class="mb-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              class={`px-2.5 py-1 text-xs rounded-md border ${trendViewMode === "revenue" ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              onClick={() => setTrendViewMode("revenue")}
            >
              Revenue Trend
            </button>
            <button
              type="button"
              class={`px-2.5 py-1 text-xs rounded-md border ${trendViewMode === "orders" ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              onClick={() => setTrendViewMode("orders")}
            >
              Order Timeline
            </button>
            <span class="mx-1 h-4 w-px bg-gray-200" />
            <button type="button" class="text-xs text-gray-600 hover:text-gray-800" onClick={selectAllDcs}>Select all DCs</button>
            <span class="text-gray-300">|</span>
            <button type="button" class="text-xs text-gray-600 hover:text-gray-800" onClick={clearAllDcs}>Clear all</button>
          </div>
          <div class="mb-3 flex flex-wrap gap-1.5">
            {dcNames.map((dc: string, idx: number) => {
              const isSelected = selectedDcSet.has(dc)
              return (
                <button
                  key={dc}
                  type="button"
                  onClick={() => toggleDcSelection(dc)}
                  class={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs transition-colors ${isSelected ? "border-gray-300 bg-gray-100 text-gray-800" : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"}`}
                >
                  <span class="h-2 w-2 rounded-full" style={{ backgroundColor: DC_COLORS[idx % DC_COLORS.length] }} />
                  {dc}
                </button>
              )
            })}
          </div>
          <div class="h-[300px]">
            {!visibleDcNames.length ? (
              <div class="h-full flex items-center justify-center text-sm text-gray-500">
                Select at least one DC to show trend data.
              </div>
            ) : trendViewMode === "revenue" ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickFormatter={(val: string) => {
                      try { return format(parseISO(val), "MMM yy") } catch { return val }
                    }}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tickFormatter={formatAmount}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11 }}
                    width={55}
                  />
                  <Tooltip
                    content={({ active, payload, label }: any) => {
                      if (!active || !payload?.length) return null
                      const sorted = [...payload].sort((a: any, b: any) => (b.value || 0) - (a.value || 0))
                      let monthLabel = label
                      try { monthLabel = format(parseISO(label), "MMMM yyyy") } catch { /* fallback */ }
                      return (
                        <div class="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
                          <p class="font-semibold mb-2">{monthLabel}</p>
                          {sorted.map((entry: any, idx: number) => (
                            <div key={idx} class="flex items-center justify-between gap-4">
                              <div class="flex items-center gap-1.5">
                                <div class="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                                <span class="text-gray-700">{entry.name}</span>
                              </div>
                              <span class="font-medium">{formatAmount(entry.value || 0)}</span>
                            </div>
                          ))}
                        </div>
                      )
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }} />
                  {visibleDcNames.map((dc: string, idx: number) => (
                    <Line
                      key={dc}
                      type="monotone"
                      dataKey={dc}
                      stroke={chartConfig[dc]?.color || DC_COLORS[idx % DC_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    type="number"
                    dataKey="timestamp"
                    domain={["dataMin", "dataMax"]}
                    tickFormatter={(value: number) => format(new Date(value), "MMM d")}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11 }}
                    minTickGap={22}
                  />
                  <YAxis
                    type="number"
                    dataKey="amount"
                    tickFormatter={formatAmount}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11 }}
                    width={55}
                  />
                  <Tooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    content={({ active, payload }: any) => {
                      if (!active || !payload?.length) return null
                      const point = payload[0].payload as OrderTimelinePoint
                      return (
                        <div class="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm min-w-[220px]">
                          <p class="font-semibold mb-1">{point.dc_name}</p>
                          <MetricRow label="Date" value={format(parseISO(point.invoice_date), "MMM d, yyyy")} />
                          <MetricRow label="Amount" value={formatAmountFull(point.amount)} />
                          <MetricRow label="Invoice #" value={point.invoice_number} />
                          <MetricRow label="PO #" value={point.po_number ?? "—"} />
                        </div>
                      )
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }} />
                  {visibleDcNames.map((dc: string, idx: number) => {
                    const dcPoints = visibleOrderTimelinePoints.filter((point: OrderTimelinePoint) => point.dc_name === dc)
                    if (!dcPoints.length) return null
                    return (
                      <Scatter
                        key={dc}
                        name={dc}
                        data={dcPoints}
                        dataKey="amount"
                        fill={chartConfig[dc]?.color || DC_COLORS[idx % DC_COLORS.length]}
                        stroke={chartConfig[dc]?.color || DC_COLORS[idx % DC_COLORS.length]}
                      />
                    )
                  })}
                </ScatterChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lightweight freight-efficiency view */}
      <Card>
        <CardHeader>
          <h3 class="text-base font-semibold">Ordering Cadence vs Order Size</h3>
          <p class="text-sm text-gray-500">Last 12 months · left side orders more often, higher dots are larger PO proxies</p>
        </CardHeader>
        <CardContent>
          <div class="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 8, right: 18, left: 8, bottom: 12 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  type="number"
                  dataKey="cadence_weeks"
                  name="Weeks between orders"
                  tickFormatter={formatWeeksCompact}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                  width={60}
                />
                <YAxis
                  type="number"
                  dataKey="order_size"
                  name="Avg amount / PO"
                  tickFormatter={formatAmount}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                  width={58}
                />
                <ZAxis type="number" dataKey="total_amount" range={[70, 420]} />
                <Tooltip
                  cursor={{ strokeDasharray: "3 3" }}
                  content={({ active, payload }: any) => {
                    if (!active || !payload?.length) return null
                    const dc = payload[0].payload as CadencePoint
                    return (
                      <div class="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm min-w-[210px]">
                        <div class="flex items-center gap-2 mb-2">
                          <div class="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: dc.color }} />
                          <p class="font-semibold">{dc.dc_name}</p>
                        </div>
                        <MetricRow label="Avg gap" value={formatWeeks(dc.avg_weeks_between_orders)} />
                        <MetricRow label="Normalized" value={formatWeeks(dc.normalized_weeks_per_order)} />
                        <MetricRow label="Avg / PO" value={formatAmountFull(dc.avg_amount_per_order)} />
                        <MetricRow label="Orders" value={dc.order_count.toLocaleString()} />
                        <MetricRow label="Total" value={formatAmountFull(dc.total_amount)} />
                      </div>
                    )
                  }}
                />
                <Scatter data={cadenceChartData}>
                  {cadenceChartData.map((entry: CadencePoint) => (
                    <Cell key={entry.dc_name} fill={entry.color} fillOpacity={0.78} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Sortable totals table */}
      <Card>
        <CardHeader>
          <h3 class="text-base font-semibold">Totals by DC</h3>
          <p class="text-sm text-gray-500">Last 12 months · same window as the map and trend chart</p>
        </CardHeader>
        <CardContent>
          <TooltipProvider>
            <div class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b border-gray-200">
                    <SortableHeader field="dc_name" current={sortField} dir={sortDir} onSort={handleSort}>DC</SortableHeader>
                    <SortableHeader field="dc_number" current={sortField} dir={sortDir} onSort={handleSort} align="right">DC #</SortableHeader>
                    <SortableHeader field="total_amount" current={sortField} dir={sortDir} onSort={handleSort} align="right">Total Amount</SortableHeader>
                    <SortableHeader field="order_count" current={sortField} dir={sortDir} onSort={handleSort} align="right">PO Count</SortableHeader>
                    <SortableHeader field="invoice_count" current={sortField} dir={sortDir} onSort={handleSort} align="right">Invoice Count</SortableHeader>
                    <SortableHeader field="avg_weeks_between_orders" current={sortField} dir={sortDir} onSort={handleSort} align="right">
                      <HeaderLabelWithInfo
                        label="Avg Gap"
                        tooltip="Historical average weeks between distinct order dates."
                      />
                    </SortableHeader>
                    <SortableHeader field="normalized_weeks_per_order" current={sortField} dir={sortDir} onSort={handleSort} align="right">
                      <HeaderLabelWithInfo
                        label="Norm. Cadence"
                        tooltip="Weeks elapsed in the selected period divided by PO count."
                      />
                    </SortableHeader>
                    <SortableHeader field="avg_amount_per_order" current={sortField} dir={sortDir} onSort={handleSort} align="right">
                      <HeaderLabelWithInfo
                        label="Avg / PO"
                        tooltip="Total Amount divided by PO Count for the selected period."
                      />
                    </SortableHeader>
                  </tr>
                </thead>
                <tbody>
                  {sortedByDc.map((dc: DcTotalPoint, idx: number) => {
                    // find color using original index from data.by_dc for consistent colors with map/chart
                    const originalIdx = data.by_dc.findIndex((d: DcTotalPoint) => d.dc_name === dc.dc_name)
                    return (
                      <tr key={dc.dc_name} class="border-b border-gray-100 hover:bg-gray-50">
                        <td class="py-2 px-3">
                          <div class="flex items-center gap-2">
                            <div class="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: DC_COLORS[originalIdx % DC_COLORS.length] }} />
                            {dc.dc_name}
                          </div>
                        </td>
                        <td class="py-2 px-3 text-right text-gray-500">{dc.dc_number ?? "—"}</td>
                        <td class="py-2 px-3 text-right font-medium">{formatAmount(dc.total_amount)}</td>
                        <td class="py-2 px-3 text-right text-gray-600">{dc.order_count.toLocaleString()}</td>
                        <td class="py-2 px-3 text-right text-gray-600">{dc.invoice_count.toLocaleString()}</td>
                        <td class="py-2 px-3 text-right text-gray-600" title="Historical average interval between distinct order dates">{formatWeeks(dc.avg_weeks_between_orders)}</td>
                        <td class="py-2 px-3 text-right text-gray-600" title="Weeks elapsed in the selected period divided by PO count">{formatWeeks(dc.normalized_weeks_per_order)}</td>
                        <td class="py-2 px-3 text-right text-gray-600">{formatAmountFull(dc.avg_amount_per_order)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </TooltipProvider>
        </CardContent>
      </Card>
    </div>
  )
}

function formatWeeks(value: number | null | undefined): string {
  if (value == null) return "—"
  if (value < 1) return `${value.toFixed(1)} wk`
  return `${value.toFixed(value >= 10 ? 0 : 1)} wks`
}

function formatWeeksCompact(value: number): string {
  return `${value.toFixed(value >= 10 ? 0 : 1)}w`
}

function formatAmountFull(value: number | null | undefined): string {
  if (value == null) return "—"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value)
}

interface MetricRowProps {
  label: string
  value: string
}

function MetricRow({ label, value }: MetricRowProps) {
  return (
    <div class="flex items-center justify-between gap-4">
      <span class="text-gray-500">{label}</span>
      <span class="font-medium text-gray-900">{value}</span>
    </div>
  )
}

interface SortableHeaderProps {
  field: DcSortField
  current: DcSortField
  dir: SortDir
  onSort: (f: DcSortField) => void
  align?: "left" | "right"
  children: any
}

interface HeaderLabelWithInfoProps {
  label: string
  tooltip: string
}

function HeaderLabelWithInfo({ label, tooltip }: HeaderLabelWithInfoProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false)

  return (
    <span class="inline-flex items-center gap-1">
      <span>{label}</span>
      <UiTooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            class="inline-flex items-center text-gray-400 hover:text-gray-600"
            aria-label={`More info about ${label}`}
            onClick={(event: MouseEvent) => event.stopPropagation()}
            onMouseEnter={() => setTooltipOpen(true)}
            onMouseLeave={() => setTooltipOpen(false)}
            onFocus={() => setTooltipOpen(true)}
            onBlur={() => setTooltipOpen(false)}
          >
            <Info className="h-3 w-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-64 text-xs">
          <p>{tooltip}</p>
        </TooltipContent>
      </UiTooltip>
    </span>
  )
}

function SortableHeader({ field, current, dir, onSort, align = "left", children }: SortableHeaderProps) {
  const isActive = current === field
  const Icon = isActive ? (dir === "asc" ? ArrowUpIcon : ArrowDownIcon) : ChevronsUpDownIcon
  const alignClass = align === "right" ? "justify-end text-right" : "justify-start text-left"
  const iconClass = isActive ? "h-4 w-4" : "ml-2 h-4 w-4"

  return (
    <th
      class="py-2 px-3 font-medium text-gray-600 cursor-pointer select-none hover:bg-gray-50"
      onClick={() => onSort(field)}
    >
      <div class={`inline-flex items-center h-6 w-full cursor-pointer text-sm whitespace-nowrap ${alignClass}`}>
        <span>{children}</span>
        <Icon class={iconClass} />
      </div>
    </th>
  )
}
